import { FormValidator, ValidationResult } from '../formField/types/form-validator';
import { PhoneNumber, PhoneNumberValidationStrings } from '../../gzPhoneInput/types';
import { isEmpty } from 'lodash';
import { phoneCodes } from '../../gzPhoneInput/utils/phone-codes';
import { getPhoneInputValidationDefaultStrings } from '../../gzPhoneInput/types/strings';

export interface PhoneNumberValidatorConfig {
  isRequired?: boolean;
  validationTexts?: PhoneNumberValidationStrings;
}

// The maximum length of a phone number is 15 characters, including the digits from country code
const MAX_PHONE_NUMBER_LENGTH = 15;
// The maximum length of the phone input is 14 characters (without the country code)
const MAX_PHONE_INPUT_LENGTH = 14;
// The minimum length of the phone input is 3 characters (without the country code)
const MIN_PHONE_INPUT_LENGTH = 3;

export const PhoneNumberValidator: (config: PhoneNumberValidatorConfig) => FormValidator = (
  config: PhoneNumberValidatorConfig
) => {
  const defaultStrings = getPhoneInputValidationDefaultStrings();
  return async (value: PhoneNumber): Promise<ValidationResult> => {
    if (isOptionalAndEmpty(value, config.isRequired)) {
      return { valid: true };
    }

    if (isRequiredAndEmpty(value, config.isRequired) || isPartiallyFilled(value)) {
      return {
        valid: false,
        error: config.validationTexts?.mandatoryErrorText ?? defaultStrings.mandatoryErrorText,
      };
    }

    if (isInvalidPhoneLength(value) || isInvalidCountryCode(value.countryCode)) {
      return {
        valid: false,
        error: config.validationTexts?.invalidPhoneErrorText ?? defaultStrings.invalidPhoneErrorText,
      };
    }

    if (isInvalidPhoneInputLength(value.subscriberNumber) || !isNumeric(value.subscriberNumber)) {
      return {
        valid: false,
        error: config.validationTexts?.invalidInputValueErrorText ?? defaultStrings.invalidInputValueErrorText,
      };
    }

    return { valid: true };
  };
};

function isOptionalAndEmpty(value: PhoneNumber, isRequired?: boolean): boolean {
  const isEmptyCountryCode = value.countryCode.trim() === '';
  const isEmptySubscriberNumber = value.subscriberNumber.trim() === '';

  return !isRequired && isEmptyCountryCode && isEmptySubscriberNumber;
}

function isRequiredAndEmpty(value: PhoneNumber, isRequired?: boolean): boolean {
  const isEmptyCountryCode = value.countryCode.trim() === '';
  const isEmptySubscriberNumber = value.subscriberNumber.trim() === '';

  return !!isRequired && isEmptyCountryCode && isEmptySubscriberNumber;
}

function isPartiallyFilled(value: PhoneNumber): boolean {
  const isEmptyCountryCode = value.countryCode.trim() === '';
  const isEmptySubscriberNumber = value.subscriberNumber.trim() === '';

  return (isEmptyCountryCode && !isEmptySubscriberNumber) || (!isEmptyCountryCode && isEmptySubscriberNumber);
}

function isInvalidPhoneLength(value: PhoneNumber): boolean {
  const totalNumberLength = value.subscriberNumber.length + value.countryCode.length;
  return totalNumberLength >= MAX_PHONE_NUMBER_LENGTH;
}

function isInvalidCountryCode(countryCode: string): boolean {
  return !isEmpty(countryCode) && !phoneCodes.find(code => code.countryCode === countryCode);
}

function isInvalidPhoneInputLength(inputValue: string): boolean {
  return inputValue.length < MIN_PHONE_INPUT_LENGTH || inputValue.length > MAX_PHONE_INPUT_LENGTH;
}

function isNumeric(value: string): boolean {
  return value === '' || /^\d+$/.test(value);
}
